/**
 * CR413/CR415: Environment Selector for Deal Card Publishing
 * 
 * Shared environment dropdown used across Deal Card View,
 * Configure Data Elements, and Components tabs.
 * CR415: All environments enabled for publish + promote.
 */

import { Globe } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import type { PublishEnvironment } from "@/hooks/usePromoteDealCardConfig";

// Re-export for convenience
export type { PublishEnvironment };

// ================================================================
// ENVIRONMENT DEFINITIONS
// ================================================================

interface EnvironmentDef {
  value: PublishEnvironment;
  label: string;
  dotClass: string;
}

const ENVIRONMENTS: EnvironmentDef[] = [
  { value: "staging", label: "Staging", dotClass: "bg-blue-500" },
  { value: "preview", label: "Admin", dotClass: "bg-amber-500" },
  { value: "production", label: "Live", dotClass: "bg-green-500" },
];

// ================================================================
// SELECTOR COMPONENT
// ================================================================

interface EnvironmentSelectorProps {
  value: PublishEnvironment;
  onChange: (env: PublishEnvironment) => void;
  disabled?: boolean;
  className?: string;
  /** Compact mode — no label, smaller trigger */
  compact?: boolean;
}

export function EnvironmentSelector({
  value,
  onChange,
  disabled = false,
  className,
  compact = false,
}: EnvironmentSelectorProps) {
  const currentEnv = ENVIRONMENTS.find(e => e.value === value) ?? ENVIRONMENTS[0];

  return (
    <div className={cn("flex items-center gap-2", className)}>
      {!compact && (
        <span className="text-sm font-medium text-muted-foreground">Environment:</span>
      )}
      <Select
        value={value}
        onValueChange={(v) => onChange(v as PublishEnvironment)}
        disabled={disabled}
      >
        <SelectTrigger className={cn(compact ? "w-[130px] h-7 text-xs" : "w-[160px] h-8")}>
          <SelectValue>
            <div className="flex items-center gap-2">
              <div className={cn("w-2 h-2 rounded-full", currentEnv.dotClass)} />
              {currentEnv.label}
            </div>
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          {ENVIRONMENTS.map((env) => (
            <SelectItem
              key={env.value}
              value={env.value}
              className="text-sm"
            >
              <div className="flex items-center gap-2">
                <div className={cn("w-2 h-2 rounded-full", env.dotClass)} />
                <span>{env.label}</span>
              </div>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

// ================================================================
// PUBLISH TARGET INDICATOR
// ================================================================

interface PublishTargetIndicatorProps {
  environment: PublishEnvironment;
  className?: string;
}

/** Inline indicator showing which environment a tab publishes to */
export function PublishTargetIndicator({ environment, className }: PublishTargetIndicatorProps) {
  const env = ENVIRONMENTS.find(e => e.value === environment) ?? ENVIRONMENTS[0];
  return (
    <div className={cn("flex items-center gap-1.5 text-xs text-muted-foreground", className)}>
      <Globe className="h-3 w-3" />
      <span>Publish target:</span>
      <Badge variant="outline" className="text-[10px] h-5 gap-1 px-1.5">
        <div className={cn("w-1.5 h-1.5 rounded-full", env.dotClass)} />
        {env.label}
      </Badge>
    </div>
  );
}
